import React, { useEffect, useRef, useState } from "react";
import { HelpCircle, X, Send, Loader2, AlertTriangle, CheckCircle2, Paperclip, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const MAX_FILES = 5;
const MAX_BYTES = 25 * 1024 * 1024;
const ALLOWED_MIME = new Set([
  "image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "application/pdf",
]);

/**
 * iter72j · Submit a support ticket.
 *
 * Modal launched from any authenticated nav ("Contact Support"). Any
 * role (member, club staff, vendor) can file. Priority + email-updates
 * toggle exposed to the submitter.
 */
export default function SupportSubmitModal({ open, onClose, onSubmitted }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [notifyByEmail, setNotifyByEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [okTicketId, setOkTicketId] = useState(null);
  const [attachments, setAttachments] = useState([]); // {attachment_id, filename, size, content_type}
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (open) {
      setSubject(""); setBody(""); setPriority("normal");
      setNotifyByEmail(true); setError(""); setOkTicketId(null);
      setAttachments([]);
    }
  }, [open]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    for (const f of files) {
      if (attachments.length >= MAX_FILES) {
        setError(`You can attach up to ${MAX_FILES} files per ticket.`);
        break;
      }
      if (!ALLOWED_MIME.has(f.type)) {
        setError(`"${f.name}" is not a supported type (images or PDF only).`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        setError(`"${f.name}" is over the 25 MB limit.`);
        continue;
      }
      setUploading(true); setError("");
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/support/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((prev) => [...prev, data]);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally { setUploading(false); }
    }
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.attachment_id !== id));
  };

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/support/tickets", {
        subject: subject.trim(),
        body: body.trim(),
        priority,
        notify_by_email: notifyByEmail,
        attachment_ids: attachments.map((a) => a.attachment_id),
      });
      setOkTicketId(data.ticket.ticket_id);
      onSubmitted?.(data.ticket);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
         onClick={onClose} data-testid="support-submit-modal">
      <div className="bg-white max-w-lg w-full border border-[#E5E3D8] max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[#E5E3D8] flex items-start justify-between gap-3">
          <div>
            <div className="overline text-[#B59A5A] inline-flex items-center gap-1.5">
              <HelpCircle size={12} /> Support
            </div>
            <h2 className="font-serif text-2xl text-[#1A422D] mt-1">Contact Support</h2>
          </div>
          <button type="button" onClick={onClose}
                  className="text-[#7A7A7A] hover:text-[#1A422D] text-2xl leading-none">×</button>
        </div>

        <div className="p-6">
          {okTicketId ? (
            <div className="text-center py-6" data-testid="support-submit-success">
              <CheckCircle2 size={30} className="mx-auto text-[#1A422D]" />
              <div className="overline text-[#B59A5A] mt-3">Ticket filed</div>
              <h3 className="font-serif text-xl text-[#1A422D] mt-1">
                {okTicketId}
              </h3>
              <p className="text-xs text-[#7A7A7A] mt-2">
                Our support team has been notified. You&rsquo;ll get updates
                in-app and {notifyByEmail ? "by email." : "not by email — check My Tickets for status."}
              </p>
              <div className="mt-5 flex items-center justify-center gap-3">
                <a href="/support/tickets" className="text-[11px] uppercase tracking-[0.22em] text-[#1A422D] underline">
                  View my tickets
                </a>
                <button type="button" onClick={onClose}
                        data-testid="support-submit-done"
                        className="bg-[#1A422D] text-[#F9F8F4] px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#0f1a14]">
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <label className="block mb-3">
                <span className="label-elegant block mb-1">Subject</span>
                <input value={subject} onChange={(e) => setSubject(e.target.value)}
                       data-testid="support-subject" maxLength={200}
                       className="input-field" placeholder="Brief summary" />
              </label>
              <label className="block mb-3">
                <span className="label-elegant block mb-1">Describe the issue</span>
                <textarea value={body} onChange={(e) => setBody(e.target.value)}
                          data-testid="support-body" rows={6} maxLength={8000}
                          className="input-field text-sm"
                          placeholder="Steps you took, what you expected, what happened…" />
              </label>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <label className="block">
                  <span className="label-elegant block mb-1">Priority</span>
                  <select value={priority} onChange={(e) => setPriority(e.target.value)}
                          data-testid="support-priority" className="input-field text-sm">
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </label>
                <label className="flex items-start gap-2 pt-6 text-xs text-[#4A4A4A]">
                  <input type="checkbox" checked={notifyByEmail}
                         onChange={(e) => setNotifyByEmail(e.target.checked)}
                         data-testid="support-notify-email"
                         className="mt-0.5 h-4 w-4 accent-[#1A422D]" />
                  Email me status updates and replies
                </label>
              </div>

              {/* iter72k · Attachments (images + PDF up to 5 · 25MB each) */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="label-elegant">Attachments ({attachments.length}/{MAX_FILES})</span>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={attachments.length >= MAX_FILES || uploading}
                    data-testid="support-attach-btn"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#1A422D] hover:text-[#0f1a14] disabled:opacity-50"
                  >
                    {uploading ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                    {uploading ? "Uploading…" : "Attach files"}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/webp,image/gif,image/heic,application/pdf"
                    onChange={onFiles}
                    data-testid="support-attach-input"
                    className="hidden"
                  />
                </div>
                {attachments.length > 0 && (
                  <ul className="space-y-1" data-testid="support-attach-list">
                    {attachments.map((a) => (
                      <li key={a.attachment_id}
                          className="flex items-center gap-2 border border-[#E5E3D8] bg-[#FBF9F1] px-2 py-1 text-xs">
                        <Paperclip size={11} className="text-[#8a6f33]" />
                        <span className="truncate flex-1">{a.filename}</span>
                        <span className="text-[10px] text-[#7A7A7A]">{_fmtSize(a.size)}</span>
                        <button type="button" onClick={() => removeAttachment(a.attachment_id)}
                                data-testid={`support-attach-remove-${a.attachment_id}`}
                                className="text-[#8a1a1a] hover:text-[#a33]">
                          <Trash2 size={11} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[10px] text-[#7A7A7A] mt-1">Images (PNG/JPG/WEBP/GIF/HEIC) or PDF · 25 MB each</p>
              </div>

              {error && (
                <div className="border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] px-3 py-2 text-xs flex items-center gap-2 mb-3">
                  <AlertTriangle size={12} /> {error}
                </div>
              )}
              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={onClose}
                        className="text-[11px] uppercase tracking-[0.22em] text-[#7A7A7A] hover:text-[#1A422D] px-3 py-2">
                  Cancel
                </button>
                <button type="button" onClick={submit}
                        disabled={busy || !subject.trim() || !body.trim() || body.trim().length < 5}
                        data-testid="support-submit"
                        className="inline-flex items-center gap-2 bg-[#1A422D] text-[#F9F8F4] px-5 py-2 text-[11px] uppercase tracking-[0.22em] disabled:opacity-50 hover:bg-[#0f1a14]">
                  {busy && <Loader2 size={12} className="animate-spin" />}
                  <Send size={12} /> {busy ? "Sending…" : "Submit ticket"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function _fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
